import React from "react";
import { CreditHistoryDetail } from "./types";
import { formatHmsDuration } from "utils/dateUtils";

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: CreditHistoryDetail;
}

const TransactionModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  if (!isOpen) return null;

  return (
    <div
      id="default-modal"
      tabIndex={-1}
      aria-hidden="true"
      className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 md:inset-0 justify-center items-center w-full flex bg-black bg-opacity-50"
    >
      <div className="relative p-4 w-full max-w-xl">
        <div className="relative bg-white rounded-lg shadow pb-4">
          <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
            <p className="text-base font-semibold text-grease-100">
              Transaction Details
            </p>
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          <div className="bg-indigo-50 bg-opacity-60 rounded-t-xl p-4 mb-0 m-4">
            <div className="flex flex-wrap justify-between gap-4">
              <div>
                <p className="text-3xl text-royalblue-100 font-bold">
                  {transaction.creditAmount > 0
                    ? `+${transaction.creditAmount}`
                    : transaction.creditAmount}
                </p>
                <p className="text-royalblue-100 text-sm bg-white px-4 py-1 mt-2 rounded-full border border-royalblue-100 w-fit">
                  {transaction.creditType}
                </p>
              </div>
              <div className="flex flex-col items-end gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <p className="text-grease-100 text-sm">
                    {new Date(transaction.createdDate).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <i className="st-time text-lightgray-200 text-sm"></i>
                  <p className="text-lightgray-200 text-xs">
                    {new Date(transaction.createdDate).toLocaleTimeString(
                      "en-US",
                      { hour: "numeric", minute: "2-digit", hour12: true },
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-indigo-100 bg-opacity-60 rounded-b-xl p-4 mx-4">
            <p className="text-sm text-[#5A6573] text-center">
              You submitted flight loads
            </p>
          </div>

          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
              <div>
                <p className="text-[#5A6573] text-xs">Airlines</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {transaction.airlineName}
                </p>
              </div>
            </div>
            <div>
              <p className="text-[#5A6573] text-xs">Flight No.</p>
              <p className="text-grease-100 mt-1 text-sm font-medium text-end">
                {transaction.flightNumber}
              </p>
            </div>
          </div>

          <div className="bg-lightblue-100 mb-0 m-4 p-4 rounded-xl flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-grease-100 text-sm font-medium">
                {transaction.sourceCode}
              </p>
              <p className="text-[#5A6573] mt-1 text-xs">
                {transaction.departureAirportName}
              </p>
              <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                <div className="rounded-full w-4 h-4 pr-0.5 bg-gray-500 flex items-center justify-center">
                  <i className="st-top-right text-white text-0.5 font-medium"></i>
                </div>
                <p className="text-[#5A6573] text-xs">
                  {new Date(transaction.departureTime).toLocaleTimeString(
                    "en-GB",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                      timeZone: "UTC",
                    },
                  )}
                </p>
              </div>
            </div>

            <div className="w-full">
              <div className="flex items-center w-full">
                <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                    <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                  </div>
                </div>
                <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                <i className="st-flight-slim text-royalblue-100 text-base"></i>
                <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                    <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-lightgray-200 text-xs text-center mt-1">
                {formatHmsDuration(transaction.duration)}
              </p>
            </div>

            <div className="flex flex-col items-end gap-1">
              <p className="text-grease-100 text-sm font-medium">
                {transaction.destinationCode}
              </p>
              <p className="text-[#5A6573] mt-1 text-xs">
                {transaction.arrivalAirportName}
              </p>
              <div className="flex items-center justify-center gap-1 w-fit mt-0.5">
                <div className="rounded-full w-4 h-4 bg-gray-500 flex items-center justify-center">
                  <i className="st-bottom-right text-white text-0.5 font-medium"></i>
                </div>
                <p className="text-[#5A6573] text-xs">
                  {new Date(transaction.arrivalTime).toLocaleTimeString(
                    "en-GB",
                    {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                      timeZone: "UTC",
                    },
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
